/*
 * Copyright (c) 2019
 *    Reliant Business Valuation, LLC - http://reliantvalue.com
 *    Author: Xaero Zero - xaerozero@icloud.com
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {Component, Input, AfterViewInit, ElementRef, ChangeDetectorRef, OnInit, OnDestroy} from '@angular/core';
import {Stage, Report, Helpers, HistoryStage} from '../../../../../model';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { ReportProvider } from 'src/app/providers';
import { PreviewPdfModal } from '../../modals';
import { Subject } from 'rxjs';
import * as FileSaver from 'file-saver';
import { ModeService,LogService } from 'src/app/services';
import { SystemType } from 'src/app/model/enums/system.type';

@Component({
  selector: 'stage-info-content',
  templateUrl: 'stage-info-content.component.html',
  styleUrls: ['stage-info-content.component.scss']
})
export class StageInfoContentComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() hostElement: HTMLElement;

  history: HistoryStage[];
  stages: Stage[];
  currentReport: Report;
  stage: Stage;

  isLoadingPreview = false;
  preview: BsModalRef;

  private top = -100000;
  private left = -100000;

  currentMode: SystemType;
  modeSub: any;

  constructor(
    private element: ElementRef,
    private cdr: ChangeDetectorRef,
    private modalService: BsModalService,
    private reportProvider: ReportProvider,
    private modeService: ModeService,
    private logService : LogService

  ) {
  }

  ngOnInit(): void {
    this.modeSub = this.modeService.mode$.subscribe(mode => this.currentMode = mode);
  }

  ngOnDestroy(): void {
    if(this.modeSub)
      this.modeSub.unsubscribe();
  }
  ngAfterViewInit(): void {
    this.show();
    this.cdr.detectChanges();
  }

  show(): void {
    if (!this.hostElement)
      return;

    const p = this.positionElements(this.hostElement, this.element.nativeElement.children[0]);
    this.top = p.top;
    this.left = p.left;
  }

  hide(): void {
    this.top = -100000;
    this.left = -100000;
  }

  getStageById(id: string): Stage {
    return this.stages.filter(stage => stage.ID === id)[0];
  }

  private positionElements(hostEl: HTMLElement, targetEl: HTMLElement): { top: number, left: number } {
    const hostElPos = this.position(hostEl);
    const targetElWidth = targetEl.offsetWidth;
    const targetElHeight = targetEl.offsetHeight;

    const shiftHeight: any = {
      center: function (): number {
        return hostElPos.top + hostElPos.height / 2 - targetElHeight / 2;
      },
      top: function (): number {
        return hostElPos.top;
      },
      bottom: function (): number {
        return hostElPos.top + hostElPos.height;
      }
    };

    let targetElPos: { top: number, left: number };

    targetElPos = {
      top: shiftHeight['center'](),
      left: hostElPos.left - targetElWidth
    };

    return targetElPos;
  }

  private position(nativeEl: HTMLElement): { width: number, height: number, top: number, left: number } {
    let offsetParentBCR = { top: 0, left: 0 };
    const elBCR = this.offset(nativeEl);
    const offsetParentEl = this.parentOffsetEl(nativeEl);
    if (offsetParentEl !== window.document) {
      offsetParentBCR = this.offset(offsetParentEl);
      offsetParentBCR.top += offsetParentEl.clientTop - offsetParentEl.scrollTop;
      offsetParentBCR.left += offsetParentEl.clientLeft - offsetParentEl.scrollLeft;
    }

    const boundingClientRect = nativeEl.getBoundingClientRect();
    return {
      width: boundingClientRect.width || nativeEl.offsetWidth,
      height: boundingClientRect.height || nativeEl.offsetHeight,
      top: elBCR.top - offsetParentBCR.top,
      left: elBCR.left - offsetParentBCR.left
    };
  }

  private offset(nativeEl: any): { width: number, height: number, top: number, left: number } {
    const boundingClientRect = nativeEl.getBoundingClientRect();
    return {
      width: boundingClientRect.width || nativeEl.offsetWidth,
      height: boundingClientRect.height || nativeEl.offsetHeight,
      top: boundingClientRect.top + (window.pageYOffset || window.document.documentElement.scrollTop),
      left: boundingClientRect.left + (window.pageXOffset || window.document.documentElement.scrollLeft)
    };
  }

  private getStyle(nativeEl: HTMLElement, cssProp: string): string {
    if ((nativeEl as any).currentStyle) // IE
      return (nativeEl as any).currentStyle[cssProp];

    if (window.getComputedStyle)
      return (window.getComputedStyle(nativeEl) as any)[cssProp];

    return (nativeEl.style as any)[cssProp];
  }

  private isStaticPositioned(nativeEl: HTMLElement): boolean {
    return (this.getStyle(nativeEl, 'position') || 'static' ) === 'static';
  }

  private parentOffsetEl(nativeEl: HTMLElement): any {
    let offsetParent: any = nativeEl.offsetParent || window.document;
    while (offsetParent && offsetParent !== window.document && this.isStaticPositioned(offsetParent)) {
      offsetParent = offsetParent.offsetParent;
    }
    return offsetParent || window.document;
  }

  onPreviewPdf(): void {
    const config = Helpers.getModalConfig();
    config.class = 'modal-lg';
    this.isLoadingPreview = true;
    this.reportProvider.getEngagementLetterPreview(this.currentReport.ID, this.currentMode)
      .subscribe(response => {
        this.isLoadingPreview = false;
        const src = new Uint8Array(response);

        this.preview = this.modalService.show(PreviewPdfModal, config);
        const ackSubject = new Subject<Boolean>();
        this.preview.content.subject = ackSubject;
        this.preview.content.name = this.currentReport.primaryEntity();
        this.preview.content.source = src;

        ackSubject.subscribe(result => {
          if (result) {this.logService.log(result); }
        });

      }, error => {
        this.isLoadingPreview = false;
        this.logService.error(error);
      });
  }

  onDownloadPdf(): void {
    this.isLoadingPreview = true;
    if (this.currentReport.DOCUMENTS.length) {
      this.reportProvider.getDocuSignDocument(this.currentReport.ID)
        .subscribe(response => {
          this.isLoadingPreview = false;
          const blob = new Blob([response], { type: 'application/pdf' });
          if(this.currentMode === SystemType.EA){
            FileSaver.saveAs(
              blob, `Reliant Business Valuation - ${this.currentReport.primaryEntity()}.pdf`);
          }
          else {
            FileSaver.saveAs(
              blob, `Reliant Business Valuation - Engagement Letter.pdf`);
          }
        }, error => {
          this.isLoadingPreview = false;
          this.logService.error(error);
        });
    } else {
      this.reportProvider.getEngagementLetterPreview(this.currentReport.ID, this.currentMode)
        .subscribe(response => {
          this.isLoadingPreview = false;
          const blob = new Blob([response], { type: 'application/pdf' });
          if (this.currentMode === SystemType.EA) {
            FileSaver.saveAs(
              blob, `Reliant Business Valuation - ${this.currentReport.primaryEntity()}.pdf`);
          }
          else {
            FileSaver.saveAs(
              blob, `Reliant Business Valuation - Engagement Letter.pdf`);
          }

        }, error => {
          this.isLoadingPreview = false;
          this.logService.error(error);
        });
    }
  }
}
